import * as React from 'react';
import { CircledCrossIcon, DownloadInCloudIcon } from '../Icons';
import { Event, Filespace, FilespaceStorage } from 'fs-api-client';
import { httpsRegex } from '../../../utils/constants';
import { cloudProviders } from 'portal-shared-lib';
import { emitToWebservice, getRegionName } from '../../../utils/utilFunctions';

export interface InitializeFileSpaceParams {
    fileSystemName: string;
    objectStore: string;
    provider: FilespaceStorage.ProviderEnum;
    storageClass?: 'standard' | 'intelligentTiering';
    locationConstraint?: boolean;
    region?: string;
    virtualAddressing?: boolean;
    bucketName?: string;
    sandbox?: boolean;
    accessKeyId?: string;
    secretKey?: string;
    endpoint?: string;
    protocol?: string;
    blockSize?: string;
    filespaceVersion?: number;
}

interface InitializeFilespaceModalProperties {
    filespace: Filespace;
    domainName: string;
}

class RenderData {
    isLucidlinkStorage: boolean;
    provider?: FilespaceStorage.ProviderEnum;
    region?: string;
}

export class InitializeFilespaceModal extends React.Component<InitializeFilespaceModalProperties, any> {
    constructor(props: any) {
        super(props);

        this.openAppLink = this.openAppLink.bind(this);
    }
    exportRenderData(): RenderData | null {
        if (!this.props.filespace || !this.props.filespace.storage) {
            return null;
        }
        const storage = this.props.filespace.storage;
        let result: RenderData = {
            isLucidlinkStorage: storage.owner === FilespaceStorage.OwnerEnum.Lucidlink,
        };
        if (storage.region && storage.provider) {
            result.region = getRegionName(storage.provider, storage.region!);
        }
        if (storage.provider) {
            result.provider = storage.provider;
        }
        return result;
    }

    render() {
        const renderData = this.exportRenderData();
        const providerStr = (renderData && renderData.provider) ? renderData.provider : '';
        const regionStr = (renderData && renderData.region) ? '(' + renderData.region + ')' : '';

        return (
            <div className="modal fade initModal" id="initializeModal" tabIndex={-1} role="dialog" aria-labelledby="initializeModal" aria-hidden="true">
                <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
                    <div className="modal-content">
                        <div className="modal-header num">
                            <h2 className="modal-title">
                                Initialize a filespace
                            </h2>
                            <button type="button" className="close" data-dismiss="modal" aria-label="Close">
                                <span aria-hidden="true">
                                    <CircledCrossIcon width="20px" height="20px" />
                                </span>
                            </button>
                        </div>
                        <div className="warning-box">
                            <div>
                                <DownloadInCloudIcon width="27px" height="20px" />
                                <p>
                                    If you have not installed LucidLink client yet, please <a href="https://lucidlink.com/download-lucidlink-classic" target="_blank">download</a> it here.
                                </p>
                            </div>
                        </div>
                        <div className="modal-body">
                            <p>
                                <strong>Launch the LucidLink desktop client</strong>
                                Click the button below to launch the desktop client. You maybe be prompted to click Allow or OK.
                            </p>
                            {(renderData && !renderData.isLucidlinkStorage) && (
                            <p>
                                <strong>Add your cloud credentials</strong>
                                Add {providerStr} {regionStr} cloud credentials to associate with this filespace.<br />
                                <a href="https://www.lucidlink.com/credentials-faq" target="_blank">Need help finding your credentials?</a>
                            </p>)
                            }
                            <p>
                                <strong>Set up your Filespace’s root password</strong>
                                Each filespace initializes with a root user. The user has full access to filespace’s management options and data.
                            </p>
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn-short green launch-marker" data-dismiss="modal" id="launch" onClick={this.openAppLink}>Launch the desktop client</button>
                            <button type="button" className="link btn-short launch-cancel-marker" data-dismiss="modal">Cancel</button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    private openAppLink() {
        emitToWebservice(
            `Clicked the webportal “Launch the desktop client” button to initialize filespace ${this.props.filespace.name}.${this.props.domainName}`,
            Event.LogTypeEnum.Log,
            Event.EventTypeEnum.Filespace
        );
        window.location.href = this.constructInitFsLink();
    }

    private setEndpointParam(initFsParams: InitializeFileSpaceParams) {
        let endpoint = this.props.filespace.storage.endpoint;
        const matches = httpsRegex.exec(endpoint || '');

        if (matches) {
            endpoint = matches[2];

            if (matches[1] === 'http') {
                initFsParams.protocol = 'http';
            }
        }
        if (endpoint) {
            initFsParams.endpoint = endpoint;
        }
    }

    private getInitializationParameters() {
        const filespace = this.props.filespace;
        const cloudProviderData = cloudProviders[filespace.storage.provider];
        const locationConstraint = cloudProviderData.locationConstraint;
        const useVirtualAddressing = cloudProviderData.useVirtualAddressing;
        const blockSize = filespace.storage.blockSize;
        const initFsParams: InitializeFileSpaceParams = {
            fileSystemName: `${filespace.name}.${this.props.domainName}`,
            provider: filespace.storage.provider,
            region: filespace.storage.region,
            objectStore: cloudProviderData.objectStore,
        };

        if (filespace.storage._class === FilespaceStorage.ClassEnum.IntelligentTiering) {
            initFsParams.storageClass = 'intelligentTiering';
        }

        if (locationConstraint) {
            initFsParams.locationConstraint = true;
        }

        if (blockSize) {
            initFsParams.blockSize = blockSize + 'B';
        }

        if (useVirtualAddressing) {
            initFsParams.virtualAddressing = useVirtualAddressing;
        }

        this.setEndpointParam(initFsParams);

        if (filespace.storage.bucket) {
            initFsParams.bucketName = filespace.storage.bucket;
        }

        if (filespace.storage.owner === FilespaceStorage.OwnerEnum.Lucidlink) {
            initFsParams.sandbox = true;

            if (filespace.storage.accessKeyId && filespace.storage.secretAccessKey) {
                initFsParams.accessKeyId = filespace.storage.accessKeyId;
                initFsParams.secretKey = filespace.storage.secretAccessKey;
            } else {
                initFsParams.bucketName = `lucidlink-fs-${filespace.storage.region}/` +
                    `${this.props.domainName.toLowerCase()}/` +
                    `${filespace.name.toLowerCase()}`;
            }
        }

        if (filespace.version) {
            initFsParams.filespaceVersion = filespace.version;
        }

        return initFsParams;
    }

    private constructInitFsLink = () => {
        let link: string = 'lucid://lucidlink.com/fs/init?';
        const initFsParams = this.getInitializationParameters();
        link += Object.keys(initFsParams)
            .map(k => `${k}=${encodeURIComponent(initFsParams[k])}`)
            .join('&');
        return link;
    }
}



// WEBPACK FOOTER //
// ./src/webportal/components/common/modals/InitializeFilespaceModal.tsx